import {
  add,
  addQuarters,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subMilliseconds,
} from '@ardoq/date-time';
import { ChartDimensions, TimeRange, TimeScalingFunction } from '@ardoq/graph';
import { DateRangeViewSetting } from '@ardoq/api-types';
import { TimeAxisLabelInfo } from '../types';
import { expandUnits, unitQuantities } from '../consts';
import { measureGridLabelTextWidth } from '../utils';
import { TimelinesGridPrecisionUnit, UnitOfTime } from '@ardoq/api-types';
import { getTickLabel } from '../xAxis/utils';
import {
  isDateWithinRange,
  readTimelineDomainRangeViewSetting,
  timeSpan,
} from '../calculations';
import { getArdoqDateTimeWeekStartOptions } from './utils';
import { MULTI_UNIT_GRID_LABEL_PADDING } from '../xAxis/consts';

const formatTicks = (
  ticks: Date[],
  selectedGrid: TimelinesGridPrecisionUnit,
  numberOfUnitsToPreviousTick: number,
  isTopRow?: boolean
): Map<Date, string> =>
  new Map(
    ticks.map(tick => [
      tick,
      getTickLabel({
        tick,
        gridPrecision: selectedGrid,
        numberOfUnitsToPreviousTick,
        isWeekLabelInDailyGrid: isTopRow && selectedGrid === UnitOfTime.WEEK,
      }),
    ])
  );

type TimeIncrementor = (time: Date) => Date;
const incrementorEntry = (
  timespan: number,
  increment: number,
  unit: TimelinesGridPrecisionUnit
): [number, TimeIncrementor] => [
  timespan,
  tick => add(tick, { [unit]: increment }),
];
// the incrementor map, used by the axis to determine tick intervals.
// the first entry in this map with a key >= the axis range will provide the incrementor function, and thereby the tick interval.
const selectedGridIncrementors = new Map<number, TimeIncrementor>([
  incrementorEntry(unitQuantities[UnitOfTime.DAY], 1, UnitOfTime.DAY),
  incrementorEntry(unitQuantities[UnitOfTime.WEEK], 1, UnitOfTime.WEEK),
  incrementorEntry(unitQuantities[UnitOfTime.MONTH], 1, UnitOfTime.MONTH),
  incrementorEntry(unitQuantities[UnitOfTime.QUARTER], 3, UnitOfTime.MONTH),
  incrementorEntry(unitQuantities[UnitOfTime.YEAR], 1, UnitOfTime.YEAR),
]);
const getMultiUnitIncrementor =
  (numberOfUnits: number, unit: UnitOfTime): TimeIncrementor =>
  tick =>
    add(tick, { [unit]: numberOfUnits });

type TicksArgs = {
  tickBasisDomain: TimeRange;
  gridPrecision: TimelinesGridPrecisionUnit;
  numberOfUnitsForMultiUnitTick?: number;
};
export const ticks = ({
  tickBasisDomain,
  gridPrecision,
  numberOfUnitsForMultiUnitTick,
}: TicksArgs) => {
  const [start, end] = tickBasisDomain;
  const result = [];

  const tickPlacementStartDate = startOf(start, gridPrecision);
  const tickPlacementEndDate = startOf(
    nextUnit(end, gridPrecision),
    gridPrecision
  );

  const incrementor = numberOfUnitsForMultiUnitTick
    ? getMultiUnitIncrementor(numberOfUnitsForMultiUnitTick, gridPrecision)
    : selectedGridIncrementors.get(unitQuantities[gridPrecision])!;

  for (
    let current = tickPlacementStartDate;
    current <= tickPlacementEndDate;
    current = incrementor(current)
  ) {
    result.push(current);
  }
  return result;
};

const multiYearLabelWidth = measureGridLabelTextWidth('9999 - 9999');
export const getNumberOfYearsForMultiYearTick = (
  availableExpandedDomain: TimeRange,
  chartWidth: number
) => {
  const labelWidth = MULTI_UNIT_GRID_LABEL_PADDING * 2 + multiYearLabelWidth;
  const domainTimeSpan = timeSpan(availableExpandedDomain);

  // a century is an arbitrary limit, for it to not be endless
  const limit = 100;
  for (let i = 2; i <= limit; i++) {
    const numberOfMultiYearUnits =
      domainTimeSpan / (unitQuantities[UnitOfTime.YEAR] * i);

    if (chartWidth / numberOfMultiYearUnits > labelWidth) {
      return i;
    }
  }
  return limit;
};

const expandKeys = [...expandUnits.keys()];

export const nextUnit = (date: Date, unitOfTime: UnitOfTime) =>
  unitOfTime === UnitOfTime.QUARTER
    ? addQuarters(date, 1)
    : add(date, { [unitOfTime]: 1 });

export const expandDomain = (
  domain: TimeRange,
  domainRangeViewSetting?: DateRangeViewSetting
): TimeRange => {
  const range = timeSpan(domain);

  /**
   * User defines domain range with ONE_DAY precision. If start/end is not defined, use range-based precision
   */

  const domainRangeLimits = readTimelineDomainRangeViewSetting(
    domainRangeViewSetting
  );

  // if the domain is more than 3/4 of the unit of time, expand to the start and end of that unit of time.
  const expandKey = expandKeys.find(key => range >= key * 0.75);
  const rangeBasedExpandUnit = expandKey
    ? expandUnits.get(expandKey)!
    : UnitOfTime.DAY;

  const [start, end] = domain;

  /**
   * If there are domainRangeLimits specified, we cannot expand the domain freely, if they are undefined,
   * we expand the domain one unit beyond the grid unit occupied by data, i.e. create a sort of "white space"
   * padding around the phases
   */
  const [domainRangeLimitStart, domainRangeLimitEnd] = domainRangeLimits;

  const expandUnit =
    domainRangeLimitStart || domainRangeLimitEnd
      ? UnitOfTime.DAY
      : rangeBasedExpandUnit;

  const initialDomainUnitStart = startOf(start, expandUnit);
  const initialDomainUnitEnd = startOf(nextUnit(end, expandUnit), expandUnit);

  const expandedDomainStart = domainRangeLimitStart
    ? initialDomainUnitStart
    : startOf(subMilliseconds(initialDomainUnitStart, 1), rangeBasedExpandUnit);

  const expandedDomainEnd = domainRangeLimitEnd
    ? initialDomainUnitEnd
    : startOf(nextUnit(initialDomainUnitEnd, expandUnit), expandUnit);

  return [expandedDomainStart, expandedDomainEnd];
};

const startOf = (date: Date, unit: UnitOfTime) => {
  switch (unit) {
    case UnitOfTime.YEAR:
      return startOfYear(date);
    case UnitOfTime.QUARTER:
      return startOfQuarter(date);
    case UnitOfTime.MONTH:
      return startOfMonth(date);
    case UnitOfTime.WEEK:
      return startOfWeek(date, getArdoqDateTimeWeekStartOptions());
    default:
      return startOfDay(date);
  }
};

type Tick = {
  tickValue: Date;
  tickPosition: number;
  tickLabel: string;
  tickSize: number;
};

const measureAndFormatTicks = (
  ticks: Date[],
  scaler: TimeScalingFunction,
  selectedGrid: TimelinesGridPrecisionUnit,
  numberOfUnitsForMultiUnitTick: number,
  isTopRow: boolean
): TimeAxisLabelInfo[] => {
  const formattedTicks = formatTicks(
    ticks,
    selectedGrid,
    numberOfUnitsForMultiUnitTick,
    isTopRow
  );

  const measuredTicks: Tick[] = ticks.map(tick => {
    const tickLabel = formattedTicks.get(tick)!;
    return {
      tickValue: tick,
      tickPosition: scaler(tick),
      tickLabel,
      tickSize: measureGridLabelTextWidth(tickLabel),
    };
  });

  return measuredTicks;
};

type GetLabelTicksForVisibleChartDomainArgs = {
  scaleX: TimeScalingFunction;
  visibleDomain: TimeRange;
  dimensions: ChartDimensions;
  gridPrecision: TimelinesGridPrecisionUnit;
  numberOfUnitsForMultiUnitTick: number;
  isTopRow: boolean;
};
export const getLabelTicksForVisibleChartDomain = ({
  scaleX,
  visibleDomain,
  dimensions,
  gridPrecision,
  numberOfUnitsForMultiUnitTick,
  isTopRow,
}: GetLabelTicksForVisibleChartDomainArgs): TimeAxisLabelInfo[] => {
  const xLabelTickValues = ticks({
    tickBasisDomain: visibleDomain,
    gridPrecision,
    numberOfUnitsForMultiUnitTick,
  }).filter(tick => isDateWithinRange(tick, visibleDomain));

  const formattedTicks = measureAndFormatTicks(
    xLabelTickValues,
    scaleX,
    gridPrecision,
    numberOfUnitsForMultiUnitTick,
    isTopRow
  );

  const xLabelVisibleTicks = formattedTicks.filter(
    tick =>
      tick.tickPosition >= dimensions.margin.left - 1 &&
      tick.tickPosition <= dimensions.chart.width + dimensions.margin.left
  );

  return xLabelVisibleTicks;
};
